<script setup lang="ts">

import {NAVIGATE_HOME} from "~/constants/navigation/navigation_routes";
import {NAVIGATE_ORDER_SERVICE_NEW} from "~/constants/navigation/orders";
import Breadcrumbs from "~/components/component/Breadcrumbs.vue";
import {useClientTypeCookie} from "~/composables/cookie/useClientTypeCookie";
import {
  useStoreAdditionalServiceDuration,
  useStoreAdditionalServicePrice,
  useStoreSelectedService,
  useStoreSelectedServiceChildren,
  useStoreServices
} from "~/composables/store/order/serviceForm";
import {getCategoriesWithServices, useGetServices} from "~/composables/api/services/services";
import {useStoreServiceInputs} from "~/composables/store/services/service_inputs";
import {createQueryObject} from "~/utils/queryObject";
import OrderDataPrice from "~/components/component/Service/order/OrderDataPrice.vue";
import OrderDataPriceMobileTop from "~/components/component/Service/order/OrderDataPriceMobileTop.vue";
import FormDateTimePicker from "~/components/component/Service/order/InputsForms/FormDateTimePicker.vue";
import OrderPayment from "~/components/component/Service/order/forms/OrderPayment.vue";
import ConfirmContactInfo from "~/components/page/Service/order/ConfirmContactInfo.vue";
import OrderAddress from "~/components/page/Service/order/OrderAddress.vue";
import CleaningData from "~/components/page/Service/order/CleaningData.vue";
import {scrollToAnchor} from "~/utils/scrollToAnchor";
import {useUnFixHeader} from "~/composables/store/ui/header";
import {API_PAYMENT_BONUSES} from "~/constants/api/payment/payment";
import {useStoreBonuses} from "~/composables/store/order/mainForm";
definePageMeta({
  hideChatWidget: true
})
const { t } = useI18n()
const links = [
  {
    url: NAVIGATE_HOME,
    navigateTo: NAVIGATE_HOME,
    title: t('b2b_landing.b2b_page_title'),
    isHome: true
  },
  {
    url: NAVIGATE_ORDER_SERVICE_NEW,
    navigateTo: NAVIGATE_ORDER_SERVICE_NEW,
    title: t('order_page.titles.order_service'),
  }
]
const route = useRoute()

const router = useLocaleRouter()
let category_keyword = route.query.category_slug || null
let service_slug = route.params.service_slug
const serviceType = route.query.service_type || useClientTypeCookie().getClientType().value
const baseRouter = useRouter()
const services = useStoreServices()
const { isDesktop } = useDevice()
const bonuses = useStoreBonuses()

const currentForm = ref(1)
const { handleUnFixHeader, handleFixHeader } = useUnFixHeader()

onMounted(() => {
  handleUnFixHeader()
})
onBeforeRouteLeave((to, from, next) => {
  handleFixHeader()
  next();
})
if (!category_keyword) {
  const {data: response, error, pending} = await getCategoriesWithServices(serviceType)
  category_keyword = response.value.data[0].keyword
  router.push({query: {category_slug: category_keyword}})
}

function goNext(nextForm: number, anchor_id?: string) {
  currentForm.value = nextForm
  if (anchor_id) {
    scrollToAnchor(anchor_id)
  }
}
function goBack(toForm: number, anchor_id?: string) {
  currentForm.value = toForm
  if (anchor_id) {
    scrollToAnchor(anchor_id)
  }
}

await useGetServices(category_keyword, serviceType)

getBonuses()

const service = useStoreSelectedService()

const inputStore = useStoreServiceInputs()

if (service_slug) {
  service.value = services.value?.find((s: any) => s?.keyword === service_slug)
  if (!service.value) {
    service.value = services.value[0]
  }
} else {
  service.value = services.value[0]
}

inputStore.value = service.value?.input_fields

const haveFields = ref(false)

if (service.value) {
  haveFields.value = Object.keys(service.value?.input_fields).length > 0
  if (route.query.inputs){
    builderInputFields( route.query.inputs)
  }
}


function builderInputFields(query: any): void {
  if (!query) {
    return
  }
  query = JSON.parse(decodeURIComponent(query))
  Object.entries(query).map(([key, value]) => {
    if (!inputStore.value[key]) {
      return
    }
    inputStore.value[key].default_value = value;
    if (inputStore.value[key].options?.length) {
      inputStore.value[key].option = inputStore.value[key].options.find((option) => option.option_id === value)?.option;
    }
  });
}

const queryParams = computed(() => createQueryObject(inputStore.value))
watch([useStoreSelectedServiceId(), inputStore], () => {
  if (Object.keys(inputStore.value).length === 0) {
    return
  }
  const query = JSON.stringify(queryParams.value)
  builderInputFields(query)
  route.params.service_slug=service.value.keyword??''
  const oldQuery = JSON.parse(JSON.stringify(route.query))
  delete oldQuery.inputs
  baseRouter.replace({query: { inputs: query, ...oldQuery }})
}, {immediate: true, deep: true})


watch(useStoreSelectedServiceChildren(), (val) => {
  if (Object.keys(val).length) {
    useStoreAdditionalServicePrice().value = Object.values(val).reduce((acc, service) => {
      return acc + service.price * service.count
    }, 0)
    useStoreAdditionalServiceDuration().value = Object.values(val).reduce((acc, service) => {
      return acc + service.duration * service.count
    }, 0)
  }
}, {deep: true})

async function getBonuses() {
  try {
    const {data: response, error} = await useApi(API_PAYMENT_BONUSES, 'GET')
    if (response.value) {
      bonuses.value = parseInt(response.value?.bonus)
    }
  } catch (e) {
    console.log(e)
  }
}
</script>

<template>
  <Meta name="robots" content="noindex, nofollow"></Meta>
  <Title>{{ $t('order_page.titles.order_service') }}</Title>
  <section>
    <div class="container">
      <Breadcrumbs :links="links" class="mb-10"/>

      <h1 class="h1 md:mb-10 mb-5">{{ $t('order_page.titles.order_service') }}</h1>
    </div>
    <OrderDataPriceMobileTop
        v-if="!isDesktop"
        class="top-0 sticky z-[6]"
        @next="goNext(2)"
        :showOrderBtn="currentForm === 1"
    />

    <div class="flex gap-10 md:container w-full mt-[0px]">
      <div class="flex flex-col gap-2 w-full">
        <CleaningData
            id="cleaning-data"
            :services
            :order="1"
            :isFilled="currentForm > 1"
            @next="goNext(2, 'cleaning-data')"
            @back="goBack"
        />

        <template v-if="currentForm !== 1 ">
          <ConfirmContactInfo
              id="confirm-contact-info"
              :order="2"
              :isFilled="currentForm > 2"
              :hide="currentForm < 2"
              @next="goNext(3, 'order-address')"
              @back="goBack"
          />
          <OrderAddress
              id="order-address"
              :order="3"
              :isFilled="currentForm > 3"
              :hide="currentForm < 3 || currentForm>3"
              @next="goNext(4, 'order-address')"
              @back="goBack($event,'order-address')"
          />
          <FormDateTimePicker
              id="form-date-time-picker"
              :order="4"
              :isFilled="currentForm > 4"
              :hide="currentForm < 4"
              @next="goNext(5, 'order-payment')"
              @back="goBack"
          />

          <OrderPayment
              id="order-payment"
              :hide="currentForm < 5"
          />
        </template>
      </div>

      <OrderDataPrice
          v-if="isDesktop"
          class="sticky top-40 hidden lg:block"
          @next="goNext(2)"
          :showOrderBtn="currentForm === 1"
      />
    </div>
  </section>
</template>

<style scoped>

</style>